import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { appointmentResponseSchema } from "../../../services/prePlanningService.types";
import { http } from "../../../utils/httpCommon";
import { useApiUrls } from "../../useApiUrl";
import { safeParsePartial } from "../../../utils/zodSafeParse";
import { useIsFeatureFlagEnabled } from "../../../featureFlags/useIsFeatureFlagEnabled";

const APPOINTMENTS_SEARCH_TERM_KEY = "apptSearchTerm";

const appointmentsQueryKey = (
  sicId: number,
  apptSearchTerm: string | null = ""
) => ["pre-planning", sicId, "appointments", apptSearchTerm];

export function useGetAppointmentBucketData(
  sicId: number,
  apptSearchTerm = ""
) {
  const hookUrls = useApiUrls();
  const { getAppointmentBucket } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  return useQuery({
    refetchOnMount: false,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: appointmentsQueryKey(sicId, apptSearchTerm),
    queryFn: async () => {
      const url = getAppointmentBucket(sicId, apptSearchTerm);
      const { data } = await http.get(url);
      return isSafeParseEnabled
        ? safeParsePartial(
            appointmentResponseSchema,
            data,
            "useGetAppointmentBucketData"
          )
        : appointmentResponseSchema.parse(data);
    },
  });
}

export function useInvalidateQueryAppointmentBucketData() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const invalidateQueryAppointmentBucketData = (sicId: number) => {
    queryClient.invalidateQueries({
      queryKey: appointmentsQueryKey(
        sicId,
        searchParams.get(APPOINTMENTS_SEARCH_TERM_KEY) ?? ""
      ),
    });
  };

  return { invalidateQueryAppointmentBucketData };
}
